import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '@/services/api';
import { ActivityLog, ActivityLogsResponse } from '@/types';

export interface ActivityLogFilters {
  startDate: string;
  endDate: string;
  customerId?: number;
  userSearch: string;
  activityType: string;
  activityCategory: string;
  descriptionSearch: string;
  logLevel: string;
}

export const useActivityLogs = (filters: ActivityLogFilters, currentPage: number, itemsPerPage: number) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  const skip = (currentPage - 1) * itemsPerPage;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        skip,
        limit: itemsPerPage,
      };
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      // customerId가 null이면 전체 조회를 위해 undefined로 설정 (백엔드에서 null 처리)
      if (filters.customerId !== undefined && filters.customerId !== null) {
        params.customer_id = filters.customerId;
      }
      if (filters.userSearch) params.user_search = filters.userSearch;
      if (filters.activityType && filters.activityType !== 'all') params.activity_type = filters.activityType;
      if (filters.activityCategory && filters.activityCategory !== 'all') params.activity_category = filters.activityCategory;
      if (filters.descriptionSearch) params.description_search = filters.descriptionSearch;
      if (filters.logLevel && filters.logLevel !== 'all') params.log_level = filters.logLevel;

      const response: ActivityLogsResponse = await adminAPI.getActivityLogs(params);
      setLogs(response.logs);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || '로그를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filters, skip, itemsPerPage]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = useMemo(() => Math.ceil(total / itemsPerPage), [total, itemsPerPage]);

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 10;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 4);
      const endPage = Math.min(totalPages, currentPage + 5);
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  return {
    logs,
    loading,
    error,
    setError,
    total,
    totalPages,
    pageNumbers,
    fetchLogs,
  };
};

